import { createAsyncThunk, isRejected } from '@reduxjs/toolkit';
import { ParsedUrlQueryInput } from 'querystring';

import { BrowseProvider } from '../../../constants/featureFlags';
import {
  getAllProductsListingsData,
  getCategoryListingsData,
  getPartnerListingsData,
  getSearchListingsData,
} from '../../../services/browse-data-api/getListingsData';
import { transformBrowseServiceProductsToReduxActionPayload } from '../../../services/browse-data-api/transform';
import { BrowseServiceProductsData } from '../../../services/browse-data-api/types';
import { PageType } from '../../../types';
import { ReduxApplicationState } from '../../combinedReducer';
import { selectPageType } from '../../navigation';
import { selectBrowseProviderFeatureFlag, selectPartnerShortcode } from '../selectors';
import { RequestProductsActionPayload, TriggerName } from '../types';

export interface RequestProductsOptions {
  triggerName?: TriggerName;
  query: ParsedUrlQueryInput;
  cookie?: string;
  replaceProducts?: boolean;
  isHealthcheckRequest?: boolean;
}

interface ProductsAPIHandledRejection {
  isServerTimeout?: boolean;
  isClientAbort?: boolean;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions,
  { rejectValue: ProductsAPIHandledRejection }
>(
  'products/requestProducts',
  async ({ query, cookie, isHealthcheckRequest, triggerName }, { getState, rejectWithValue }) => {
    if (isHealthcheckRequest) {
      // Dynamically import json file to prevent it being included on the client. Healthcheck request only happens server side.
      const mockBrowseServiceProductsData = await import('../mocks/giftsForHerCategory.json');

      return {
        data: transformBrowseServiceProductsToReduxActionPayload(
          mockBrowseServiceProductsData as unknown as BrowseServiceProductsData,
        ),
      };
    }

    const state = getState() as ReduxApplicationState;
    const { deliveryZone, loop54UserId, featureFlags } = state.userConfiguration;
    const currencyCode = state.userConfiguration.currencyCode!;

    const commonGetDataArgs = {
      cookie,
      ...(currencyCode && { currencyCode }),
      ...(deliveryZone && { deliveryZone }),
      featureFlags,
    };
    let response;

    switch (selectPageType(state)) {
      case PageType.Search: {
        const { gaVisitorId } = state.userConfiguration;

        response = await getSearchListingsData({
          ...commonGetDataArgs,
          query,
          userId: gaVisitorId,
        });
        break;
      }
      case PageType.Category: {
        const { previewing, ...restOfQuery } = query;
        const { cognitoId, elevaateSessionId, gaVisitorId } = state.userConfiguration;
        const browseProviderFeatureFlag = selectBrowseProviderFeatureFlag(state);
        const googleRetailBrowseProvider = browseProviderFeatureFlag === BrowseProvider.GOOGLE;
        const userId = googleRetailBrowseProvider ? gaVisitorId : loop54UserId;

        response = await getCategoryListingsData({
          ...commonGetDataArgs,
          ...(previewing ? { previewing: true } : {}),
          elevaateUid: cognitoId,
          elevaateSessionId,
          query: restOfQuery,
          userId: userId,
          browseProvider: googleRetailBrowseProvider ? 'google' : 'loop54',
        });
        break;
      }
      case PageType.Partner: {
        const { shortcode, ...restOfQuery } = query;

        response = await getPartnerListingsData({
          ...commonGetDataArgs,
          query: restOfQuery,
          shortcode: shortcode ? String(shortcode) : selectPartnerShortcode(state),
          userId: loop54UserId,
        });
        break;
      }
      case PageType.AllProducts: {
        response = await getAllProductsListingsData({
          ...commonGetDataArgs,
          query,
          userId: loop54UserId,
        });
        break;
      }
      default:
        console.error('unknown page type');
    }

    if (response?.redirectUrl) {
      return { redirectUrl: response.redirectUrl };
    }

    if (!response?.data) {
      // TODO: don't pretend it's a client abort, when it's a problem with the browse-data-api response
      return rejectWithValue({ isClientAbort: true });
    }

    return {
      data: {
        ...transformBrowseServiceProductsToReduxActionPayload(response?.data),
        ...(query.term && { searchTerm: query.term as string }),
      },
      triggerName,
    } as RequestProductsActionPayload;
  },
);

export const requestProductsIsRejected = isRejected(requestProducts);
